import React from 'react';
import { SkillCategoryItem, SkillItem, SkillListVO } from '../services/aiAssistant';
import { LoadingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface SkillsDialogListParams {
  keyword?: string;
  categories?: string[];
  authFilter?: 'AUTHORIZED' | 'UNAUTHORIZED';
  statusFilter?: 'ALL' | 'DRAFT' | 'PUBLISHED';
  onlyMine?: boolean;
  pageNum?: number;
  pageSize?: number;
}

export interface SkillsDialogLoaders {
  featured: () => Promise<SkillListVO>;
  recommend: () => Promise<SkillListVO>;
  list: (params: SkillsDialogListParams) => Promise<SkillListVO>;
}

interface SkillsDialogProps {
  open: boolean;
  selectedIds: number[];
  categories: SkillCategoryItem[];
  loaders: SkillsDialogLoaders;
  submitting: boolean;
  onConfirm: (ids: number[]) => void;
  onClose: () => void;
}

type TabKey = 'ALL' | 'FEATURED' | 'RECOMMEND' | `CAT:${string}`;

const PAGE_SIZE = 20;
const SCROLL_BUFFER = 80;
const SEARCH_DEBOUNCE = 300;

const FIXED_TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: '全部' },
  { key: 'FEATURED', label: '业内精品' },
  { key: 'RECOMMEND', label: '自动推荐' }
];

function isPaginatedTab(tab: TabKey): boolean {
  return tab === 'ALL' || tab.startsWith('CAT:');
}

function isListTab(tab: TabKey): boolean {
  return isPaginatedTab(tab);
}

function passFilter(item: SkillItem): boolean {
  if (item.authorized === false) return false;
  if (item.status && item.status !== 'PUBLISHED') return false;
  return true;
}

function feMatch(item: SkillItem, keyword: string): boolean {
  if (!keyword) return true;
  const kw = keyword.toLowerCase();
  return (item.title || '').toLowerCase().includes(kw) || (item.name || '').toLowerCase().includes(kw);
}

export default function SkillsDialog({
  open,
  selectedIds,
  categories,
  loaders,
  submitting,
  onConfirm,
  onClose
}: SkillsDialogProps) {
  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>([]);
  const [currentTab, setCurrentTab] = useState<TabKey>('FEATURED');
  const [items, setItems] = useState<SkillItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');

  const listRef = useRef<HTMLDivElement | null>(null);
  const loadSeqRef = useRef(0);

  const fetchTab = useCallback(
    async (tab: TabKey, page: number, kw: string, append: boolean) => {
      const seq = ++loadSeqRef.current;
      setLoading(true);
      setLoadError(false);
      try {
        let resItems: SkillItem[] = [];
        let resHasMore = false;
        if (tab === 'FEATURED') {
          const res = await loaders.featured();
          resItems = res?.list || [];
          resHasMore = false;
        } else if (tab === 'RECOMMEND') {
          const res = await loaders.recommend();
          resItems = res?.list || [];
          resHasMore = false;
        } else {
          const params: SkillsDialogListParams = {
            pageNum: page,
            pageSize: PAGE_SIZE,
            authFilter: 'AUTHORIZED'
          };
          if (kw) params.keyword = kw;
          if (tab.startsWith('CAT:')) params.categories = [tab.slice(4)];
          const res = await loaders.list(params);
          resItems = res?.list || [];
          const total = res?.total ?? 0;
          resHasMore = page * PAGE_SIZE < total;
        }
        if (seq !== loadSeqRef.current) return;
        const filtered = resItems.filter(passFilter);
        setItems((prev) => (append ? [...prev, ...filtered] : filtered));
        setHasMore(resHasMore);
      } catch {
        if (seq !== loadSeqRef.current) return;
        setLoadError(true);
        // 错误提示由 request 拦截器统一处理
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [loaders]
  );

  // Init / open
  useEffect(() => {
    if (!open) return;
    setTempSelectedIds(selectedIds);
    setCurrentTab('FEATURED');
    setItems([]);
    setPageNum(1);
    setHasMore(false);
    setKeywordInput('');
    setAppliedKeyword('');
    fetchTab('FEATURED', 1, '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  // Debounce keyword → reset & fetch
  useEffect(() => {
    if (!open) return;
    if (keywordInput === appliedKeyword) return;
    const timer = window.setTimeout(() => {
      setAppliedKeyword(keywordInput);
      if (isListTab(currentTab)) {
        setItems([]);
        setPageNum(1);
        setHasMore(false);
        fetchTab(currentTab, 1, keywordInput, false);
      }
    }, SEARCH_DEBOUNCE);
    return () => window.clearTimeout(timer);
  }, [keywordInput, appliedKeyword, currentTab, fetchTab, open]);

  const handleSwitchTab = (tab: TabKey) => {
    if (tab === currentTab) return;
    setCurrentTab(tab);
    setItems([]);
    setPageNum(1);
    setHasMore(false);
    fetchTab(tab, 1, appliedKeyword, false);
  };

  const handleScroll = () => {
    if (!isPaginatedTab(currentTab) || loading || !hasMore) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_BUFFER) {
      const next = pageNum + 1;
      setPageNum(next);
      fetchTab(currentTab, next, appliedKeyword, true);
    }
  };

  const toggleSelect = (id: number) => {
    setTempSelectedIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  const displayItems = useMemo(() => {
    if (isListTab(currentTab)) return items;
    return items.filter((it) => feMatch(it, keywordInput));
  }, [items, currentTab, keywordInput]);

  const handleRetry = () => {
    setItems([]);
    setPageNum(1);
    fetchTab(currentTab, 1, appliedKeyword, false);
  };

  const handleCancel = () => {
    if (submitting) return;
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={handleCancel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 1200
        }}
      />
      <div
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1201,
          width: 'min(900px, 92vw)',
          height: 'min(640px, 85vh)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            flexShrink: 0
          }}
        >
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1d2129' }}>选择 Skills</div>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: '#8c8c8c',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Sidebar */}
          <div
            style={{
              width: 200,
              borderRight: '1px solid #f0f0f0',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                borderRadius: 8,
                background: '#f5f5f7',
                marginBottom: 10
              }}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ color: '#bfbfbf' }}>
                <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.3" fill="none" />
                <path d="M9.2 9.2L12 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              <input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="搜索 Skill..."
                style={{
                  flex: 1,
                  fontSize: 12,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  minWidth: 0
                }}
              />
            </div>
            {FIXED_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => handleSwitchTab(t.key)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  textAlign: 'left',
                  fontSize: 13,
                  border: 'none',
                  cursor: 'pointer',
                  background: currentTab === t.key ? '#E6F0FF' : 'transparent',
                  color: currentTab === t.key ? '#1677ff' : '#1d2129',
                  fontWeight: currentTab === t.key ? 600 : 400
                }}
                onMouseEnter={(e) => {
                  if (currentTab !== t.key) e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  if (currentTab !== t.key) e.currentTarget.style.background = 'transparent';
                }}
              >
                {t.label}
              </button>
            ))}
            <div style={{ height: 1, background: '#f0f0f0', margin: '8px 0' }} />
            {categories.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#bfbfbf' }}>分类加载失败</div>
            ) : (
              categories.map((c) => {
                const key: TabKey = `CAT:${c.code}`;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSwitchTab(key)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      textAlign: 'left',
                      fontSize: 13,
                      border: 'none',
                      cursor: 'pointer',
                      background: currentTab === key ? '#E6F0FF' : 'transparent',
                      color: currentTab === key ? '#1677ff' : '#4e5969',
                      fontWeight: currentTab === key ? 600 : 400
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== key) e.currentTarget.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== key) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {c.displayName}
                  </button>
                );
              })
            )}
          </div>

          {/* Grid */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 18,
              background: '#fafafb'
            }}
          >
            {loadError ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c', fontSize: 13 }}>
                <div style={{ marginBottom: 12 }}>加载失败</div>
                <button
                  type="button"
                  onClick={handleRetry}
                  style={{
                    padding: '6px 18px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    fontSize: 12,
                    color: '#1d2129',
                    cursor: 'pointer'
                  }}
                >
                  重试
                </button>
              </div>
            ) : displayItems.length === 0 && !loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#bfbfbf', fontSize: 13 }}>暂无数据</div>
            ) : (
              <>
                {displayItems.map((s) => {
                  const checked = tempSelectedIds.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      onClick={() => toggleSelect(s.id)}
                      style={{
                        position: 'relative',
                        background: '#fff',
                        borderRadius: 10,
                        padding: 16,
                        marginBottom: 12,
                        border: checked ? '1.5px solid #1677ff' : '1.5px solid transparent',
                        boxShadow: checked ? '0 0 0 3px rgba(22,119,255,0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: '#FFF0E5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FF7A1A',
                            fontSize: 14,
                            flexShrink: 0,
                            overflow: 'hidden'
                          }}
                        >
                          {s.iconUrl ? <img src={s.iconUrl} alt="" style={{ width: '100%', height: '100%' }} /> : '✦'}
                        </div>
                        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#1d2129' }}>
                          {s.title || s.name}
                        </div>
                        {s.category && (
                          <span
                            style={{
                              fontSize: 11,
                              color: '#1677ff',
                              background: '#E6F0FF',
                              padding: '3px 8px',
                              borderRadius: 4
                            }}
                          >
                            {s.category}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#4e5969', lineHeight: 1.55, marginBottom: 10 }}>
                        {s.description}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 11,
                          color: '#8c8c8c'
                        }}
                      >
                        <span>{s.creatorName}</span>
                        <div style={{ display: 'flex', gap: 14 }}>
                          <span>⚡ {s.invokeCount ?? 0}</span>
                          <span>🔗 {s.refCount ?? 0}</span>
                        </div>
                      </div>
                      {checked && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: '#1677ff',
                            color: '#fff',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
                {loading && (
                  <div style={{ textAlign: 'center', padding: 16, color: '#8c8c8c', fontSize: 12 }}>
                    <LoadingOutlined /> 加载中...
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <span style={{ fontSize: 13, color: '#8c8c8c' }}>
            已选 <b style={{ color: '#1d2129' }}>{tempSelectedIds.length}</b> 项
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '8px 24px',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                fontSize: 13,
                color: '#1d2129',
                background: '#fff',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              取消
            </button>
            <button
              type="button"
              onClick={() => onConfirm(tempSelectedIds)}
              disabled={submitting}
              style={{
                padding: '8px 24px',
                borderRadius: 6,
                fontSize: 13,
                color: '#fff',
                background: '#1677ff',
                border: 'none',
                fontWeight: 500,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {submitting && <LoadingOutlined />}
              确认选择
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
